import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAdminPlaybooks, type AdminPlaybookRow } from "@/queries/adminPlaybooks";

import { PlaybookRow } from "./PlaybookRow";
import { PlaybookEditDialog } from "./PlaybookEditDialog";

export function AdminPlaybooksPage() {
  const { t } = useTranslation("admin-playbooks");
  const { data, isPending, isError } = useAdminPlaybooks();
  const [editTarget, setEditTarget] = useState<AdminPlaybookRow | null>(null);

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{t("page.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("page.subtitle")}</p>
      </header>

      {isPending ? (
        <p className="text-sm text-muted-foreground">{t("page.loading")}</p>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertDescription>{t("page.loadError")}</AlertDescription>
        </Alert>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("page.empty")}</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("columns.name")}</TableHead>
                <TableHead>{t("columns.category")}</TableHead>
                <TableHead className="w-12 text-right">
                  {t("columns.schemaVersion")}
                </TableHead>
                <TableHead>{t("columns.status")}</TableHead>
                <TableHead>{t("columns.liveAllowed")}</TableHead>
                <TableHead>{t("columns.positionSizeOverride")}</TableHead>
                <TableHead className="w-32 text-right">
                  {t("columns.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <PlaybookRow
                  key={row.id}
                  row={row}
                  onEdit={() => setEditTarget(row)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {editTarget ? (
        <PlaybookEditDialog
          open
          onOpenChange={(next) => !next && setEditTarget(null)}
          playbook={editTarget}
        />
      ) : null}
    </div>
  );
}
